import { useCallback, useEffect, useState } from 'react';

import { Button, ContentReveal } from '@farmersdog/corgi-x';

import { trackCheckboxErrorMessage } from '../../../analytics/events/trackCheckboxErrorMessage';
import { useGetTreatment } from '../../../hooks/experiments/useGetTreatment';
import { getPetNamesFromFormEntries } from '../../../utils';

import styles from './SubmitButton.css';
import { TermsAndConditionsCheckbox } from './TermsAndConditionsCheckbox';
import { useTermsAcceptance } from './useTermsAcceptance';

import type { BranchNode } from '../../../blueprint/types';
import type { TOSAComponentInput, TOSALeafNode } from '../../../types';

export interface SubmitButtonProps
  extends TOSAComponentInput<TOSALeafNode | BranchNode> {
  loading: boolean;
  fixedButton?: boolean;
  smallWhiteSpace?: boolean;
}

export function SubmitButton(props: SubmitButtonProps) {
  const { node, progress, formMethods, schema } = props;
  const isBranchComplete = progress.getBranchComplete(node.__self__);
  useEffect(() => {
    void formMethods.trigger();
  }, [schema, formMethods]);

  if (!isBranchComplete) {
    return null;
  }

  return <SubmitButtonInner {...props} />;
}

function SubmitButtonInner(props: SubmitButtonProps) {
  const containerClassName = props.smallWhiteSpace
    ? styles.smallWhiteSpace
    : styles.additionalWhiteSpace;

  const getTreatment = useGetTreatment();
  const shouldShowTermsCheckbox =
    props.node.name === 'Me3' &&
    getTreatment('cvr_me_page_terms_privacy_checkbox').treatment === 'on';

  const [isTermsAndConditionsChecked, setIsTermsAndConditionsChecked] =
    useTermsAcceptance();
  const [hasAttemptedToSubmit, setHasAttemptedToSubmit] = useState(false);

  const { isValid, isSubmitting } = props.formMethods.formState;

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setHasAttemptedToSubmit(true);

      if (shouldShowTermsCheckbox && !isTermsAndConditionsChecked) {
        e.preventDefault();

        trackCheckboxErrorMessage({
          component: 'checkbox',
          module_name: 'terms_checkbox',
        });
      }
    },
    [shouldShowTermsCheckbox, isTermsAndConditionsChecked]
  );

  const { petNames } = getPetNamesFromFormEntries({
    formValues: props.formMethods.getValues(),
    currentNodeName: props.node.name,
  });

  return (
    <div className={containerClassName}>
      <ContentReveal in={true} inAnimation="fade">
        <div className={styles.innerContainer}>
          {shouldShowTermsCheckbox && (
            <TermsAndConditionsCheckbox
              checked={isTermsAndConditionsChecked}
              onChange={setIsTermsAndConditionsChecked}
              shouldShowError={
                hasAttemptedToSubmit && !isTermsAndConditionsChecked
              }
              petName={petNames.length > 0 ? petNames[0] : undefined}
            />
          )}

          <Button
            aria-disabled={!isValid || isSubmitting}
            className={styles.button}
            loading={props.loading}
            type="submit"
            variant="special"
            onClick={onClick}
          >
            {'Continue'}
          </Button>
        </div>
      </ContentReveal>
    </div>
  );
}
